import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { ReactComponent as SlideIcon } from '@assets/icons/order/slide-icon.svg';

import CustomDropdown from '@components/CustomDropdown';
import { toastError } from '@components/Toast';

import BlockUsedFrom from '@features/measurement/components/BlockUsedFrom';
import PomViewModal from '@features/pom/components/PomViewModal';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';
import {
	modifyMeasurementTableCellValue,
	modifyMeasurementTablePointCode,
	modifyMeasurementTablePointIds,
	modifyMeasurementTablePointName,
	pasteMeasurementSizeValues,
	reArrangeMeasurementTable,
	removePointInMeasurementTable,
	saveProductMeasurementBulkChangeLog,
	saveProductMeasurementChangeLog
} from '@store/actions/MeasurementAction';
import { getPomAndSize } from '@store/reducers';

import { getAllPomResponse } from '@services/pomService';

import { checkAnyUserType, onGetLocalFromUtc, validateFloatNumber } from '@utils/Util';

const BuyerAndNitexMeasurementTable = ({
	isMeasurementEditable,
	tableData,
	initialMeasurementTableData,
	fitWiseSizeList,
	isShowSlideIcon = false,
	hasShowActualAndDiffColumn = false,
	onDuplicateMeasurement,
	fitId,
	onReplacePOM,
	showReplaceDropdownId,
	setShowReplaceDropdownId,
	pomAndSize
}) => {
	const [selectedFitTypeId, setSelectedFitTypeId] = useState(null);
	const pomAndSizeList = useSelector(getPomAndSize)?.measurementInfo || {};
	const [selectedSizeId, setSelectedSizeId] = useState(null);
	const [arrowDisable, setArrowDisable] = useState(true);
	const [selectedRow, setSelectedRow] = useState({});
	const [selectedPom, setSelectedPom] = useState(null);
	const [allPomList, setAllPomList] = useState([]);
	const sizeDropDownRef = useRef(null);
	const elementRef = useRef(null);
	const dispatch = useDispatch();

	useEffect(() => {
		onFetchPomList();
	}, []);

	const onFetchPomList = async () => {
		try {
			dispatch(openLoader());
			const data = await getAllPomResponse();
			setAllPomList(data);
			dispatch(closeLoader());
		} catch (error) {
			dispatch(closeLoader());
			toastError(error.message);
		}
	};

	let allPoms = allPomList?.map((item) => {
		return {
			id: item.id,
			referenceId: item.referenceId,
			value: `${item.pomCode}: ${item.name}`
		};
	});

	const getAllPomOptions = () => {
		let allPomOptionsList = allPoms || [];
		let selectedPoms = tableData
			?.map((item) => item?.pomResponse?.referenceId)
			?.filter((referenceId) => referenceId);
		return allPomOptionsList.filter((pom) => !selectedPoms.includes(pom.referenceId));
	};

	useEffect(() => {
		document.addEventListener('mousedown', handleOutsideClick);
		return () => {
			document.removeEventListener('mousedown', handleOutsideClick);
		};
	}, []);

	const handleOutsideClick = (event) => {
		if (sizeDropDownRef && !sizeDropDownRef.current?.contains(event.target)) {
			setSelectedSizeId(null);
			setSelectedFitTypeId(null);
		}
	};

	const isEditable = () => {
		return isMeasurementEditable;
	};

	const isAllowEdit = () => checkAnyUserType(['ADMIN', 'GARMENTS_TECH']);

	const isInputDisabled = (obj) => {
		return ['APPROVED', 'REJECTED'].includes(obj?.status) && !isAllowEdit();
	};

	const onPressKey = async (event, obj, fitType = 'FIRST_FIT', logType, item) => {
		const isUp = event.key === 'ArrowUp';
		const isDown = event.key === 'ArrowDown';
		const isTab = event.key === 'Tab';
		const isEnter = event.key === 'Enter';

		if (!(isUp || isDown || isTab || isEnter)) return;
		event.preventDefault();
		let pomResponseId = obj.pomResponseId;

		if (!pomResponseId) return;
		let { name } = event.target;

		let focusAblePomId = null;

		if (isUp || isDown || isEnter) {
			let currentIndex = tableData.findIndex((item) => item.pomResponse.id === pomResponseId);
			if (isUp && currentIndex > 0) {
				focusAblePomId = tableData[currentIndex - 1].pomResponse.id;
			}
			if ((isDown || isEnter) && currentIndex < tableData.length - 1) {
				focusAblePomId = tableData[currentIndex + 1].pomResponse.id;
			}
		}

		if (isTab) {
			focusAblePomId = pomResponseId;
		}

		let defaultFieldType = name;
		if (isTab) {
			if (name === 'pomName') {
				defaultFieldType = 'tolerance';
			}

			let isLastSizeSelected = false;
			if (name.startsWith('sizeValue_')) {
				let currentFitSizeList = fitWiseSizeList[fitType];
				let currentSizeIndex = name.split('sizeValue_')[1];
				let sizeValueNextIndex =
					parseInt(currentSizeIndex) < currentFitSizeList.length - 1
						? parseInt(currentSizeIndex) + 1
						: null;
				if (sizeValueNextIndex) {
					defaultFieldType = `sizeValue_${sizeValueNextIndex}`;
				}
				if (sizeValueNextIndex === null) isLastSizeSelected = true;
			}

			if (name === 'tolerance') {
				defaultFieldType = 'sizeValue_0';
			}
			if (isLastSizeSelected) {
				defaultFieldType = 'actualValue';
			}
			if (name === 'actualValue') {
				defaultFieldType = 'remarks';
			}
			if (name === 'remarks') {
				let fitSize = Object.keys(fitWiseSizeList).length;
				defaultFieldType = 'sizeValue_0';
				if (fitType === 'FIRST_FIT' && fitSize > 1) {
					fitType = 'SECOND_FIT';
				} else if (fitType === 'SECOND_FIT' && fitSize > 2) {
					fitType = 'THIRD_FIT';
				} else {
					let currentIndex = tableData.findIndex((item) => item.pomResponse.id === pomResponseId);
					if (currentIndex < tableData.length - 1) {
						focusAblePomId = tableData[currentIndex + 1].pomResponse.id;
						fitType = 'FIRST_FIT';
						defaultFieldType = 'pomName';
					} else {
						focusAblePomId = null;
						defaultFieldType = '';
					}
				}
			}
		}
		if (focusAblePomId) {
			setSelectedRow({
				pomResponseId: focusAblePomId,
				fieldType: defaultFieldType,
				fitType
			});
			if (name === 'tolerance') {
				await updateToleranceOrActualValue(
					event,
					obj.pomResponseId,
					'TOLERANCE_UPDATE',
					obj.fitTypeId
				);
			} else if (name === 'actualValue') {
				await updateToleranceOrActualValue(
					event,
					obj.pomResponseId,
					'ACTUAL_VALUE_UPDATE',
					obj.fitTypeId
				);
			} else if (name === 'remarks') {
				await handleOnBlurChangeLog(
					event,
					'POINT_REMARKS_UPDATE',
					'remarks',
					obj.pomResponseId,
					obj.fitTypeId
				);
			} else if (name === 'pomName') {
				await handleOnBlurChangeLog(
					event,
					'POINT_NAME_UPDATE',
					'name',
					obj.pomResponseId,
					obj.fitTypeId
				);
			}
			if (logType === 'MEASUREMENT_VALUE_UPDATE') {
				await onUpdateSize(
					event,
					obj.pomResponseId,
					'MEASUREMENT_VALUE_UPDATE',
					item.id,
					obj.fitTypeId
				);
			}
		}
	};

	const renderTableTitle = () => {
		return initialMeasurementTableData?.map((item, index) => {
			let isSizeExist =
				index === 0 ? fitWiseSizeList[item?.fitType?.constant]?.sizeList.length > 0 : false;
			let defaultColSpan = index === 0 ? (isSizeExist ? 7 : 6) : 4;
			if (!hasShowActualAndDiffColumn) {
				defaultColSpan = defaultColSpan - 2;
			}
			return (
				<th colSpan={defaultColSpan} key={`table_heading_${index}`}>
					<div className="d-flex justify-content-between">
						<p className="regular-14 weight-500 gray_dark_01 d-flex">
							{item?.fitType?.name}{' '}
							{pomAndSizeList?.fittingName ? `- ${pomAndSizeList?.fittingName}` : ''}
							{pomAndSize?.showBlockUsedFromInfo && (
								<BlockUsedFrom pomAndSizeList={pomAndSize} />
							)}
						</p>

						{['APPROVED', 'REJECTED'].includes(item.status) && (
							<p className="regular-14 weight-500 gray_dark_01">
								{item.status === 'APPROVED' ? 'Approved' : 'Rejected'} By {item?.reviewedBy} -{' '}
								{onGetLocalFromUtc(item?.reviewedAt, 'DD MMM, YYYY')} at{' '}
								{onGetLocalFromUtc(item?.reviewedAt, 'h:mm a')}
							</p>
						)}
					</div>
				</th>
			);
		});
	};

	const updateToleranceOrActualValue = async (e, pointId, logType, fitTypeId) => {
		let newValue = e.target?.value ? Number(e.target.value) : '';
		if (isEditable() && selectedRow.defaultValue !== newValue) {
			let changeLogInfo = {
				changeLogType: logType,
				id: fitTypeId,
				dto: {
					pointOfMeasurementId: pointId,
					value: e.target.value || null
				}
			};
			await dispatch(saveProductMeasurementChangeLog(changeLogInfo));
		}
	};

	const onUpdateSize = async (e, pointId, logType, sizeId, fitTypeId) => {
		e.preventDefault();
		let newValue = e.target?.value ? Number(e.target.value) : '';
		if (isEditable() && selectedRow?.defaultValue !== newValue) {
			let changeLogInfo = {
				changeLogType: logType,
				id: fitTypeId,
				dto: {
					pointOfMeasurementId: pointId,
					value: e.target.value || null,
					sizeId: sizeId
				}
			};
			await dispatch(saveProductMeasurementChangeLog(changeLogInfo));
		}
	};

	const handleSizeChange = (e, pomResponseId, fieldName, fitType, existingSizeList, sizeId) => {
		e.preventDefault();
		if (isEditable()) {
			dispatch(
				modifyMeasurementTableCellValue({
					pointId: pomResponseId,
					fieldName,
					fitType,
					value: existingSizeList?.map((item) => {
						if (item.id === sizeId) {
							item.value = e.target.value;
						}
						return item;
					})
				})
			);
		}
	};

	const isModified = (fieldName = false) => {
		return fieldName ? 'highlight-cell' : '';
	};

	const formatNumber = (num) => {
		if (num !== undefined && num !== '') {
			let result = Number(num).toFixed(2);
			result = parseFloat(result);
			return result;
		} else {
			return '';
		}
	};

	const renderTableCell = (fitInfo) => {
		if (fitInfo === undefined) return <></>;

		const renderSizeValueList = (obj, fitType) => {
			let sizeValueList = obj?.sizeValueList;
			if (sizeValueList === undefined) return <></>;
			return sizeValueList?.map((item, index) => {
				return (
					<td
						className={`target ${isModified(item?.modified || item.sizeModified)}`}
						key={`size_cell_${index}`}
						onClick={() =>
							handleSelectedRow(
								obj.pomResponseId,
								`sizeValue_${index}`,
								fitType,
								formatNumber(item.value)
							)
						}
					>
						{isShowInput(obj.pomResponseId, fitType, `sizeValue_${index}`) ? (
							<input
								type="number"
								min={1}
								disabled={isInputDisabled(obj)}
								autoFocus={true}
								name={`sizeValue_${index}`}
								value={formatNumber(item.value)}
								style={{ display: 'block' }}
								onChange={(e) =>
									handleSizeChange(
										e,
										obj.pomResponseId,
										'sizeValueList',
										fitType,
										obj?.sizeValueList,
										item.id
									)
								}
								onBlur={(e) =>
									onUpdateSize(
										e,
										obj.pomResponseId,
										'MEASUREMENT_VALUE_UPDATE',
										item.id,
										obj.fitTypeId
									)
								}
								onKeyPress={validateFloatNumber}
								onKeyDown={(e) =>
									onPressKey(e, obj, fitType, 'MEASUREMENT_VALUE_UPDATE', item)
								}
							/>
						) : (
							<p>{formatNumber(item.value)}</p>
						)}
					</td>
				);
			});
		};

		const getTotalTarget = (obj) => {
			let sizeValueList = obj?.sizeValueList;
			const baseSizeObj = sizeValueList[0];
			return baseSizeObj?.value ? Number(baseSizeObj.value) : 0;
		};

		const isShowInput = (pomResponseId, fitType, fieldType) => {
			return (
				selectedRow.pomResponseId === pomResponseId &&
				selectedRow.fitType === fitType &&
				selectedRow.fieldType === fieldType
			);
		};

		const getDifference = (obj) => {
			let total = parseFloat((obj?.actualValue || 0) - getTotalTarget(obj).toFixed(2));
			return Math.sign(total) === -1 ? total : total > 0 ? `+${total}` : '';
		};

		const getTargetValue = (existingSizeList, sizeId) => {
			const targetItem = existingSizeList?.find((item) => item?.id === sizeId);

			// If targetItem exists, return its value, otherwise return undefined or any fallback value.
			return targetItem ? targetItem.value : undefined;
		};

		return Object?.keys(fitInfo)?.map((item, index) => {
			let fitType = item;
			let obj = fitInfo[item];
			let difference = obj?.actualValue ? getDifference(obj) : '';
			return (
				<React.Fragment key={`table_cel_${index}`}>
					{index === 0 && (
						<td
							onClick={() =>
								handleSelectedRow(
									obj.pomResponseId,
									'tolerance',
									fitType,
									formatNumber(obj?.tolerance)
								)
							}
							className={isModified(obj?.toleranceModified)}
						>
							{isShowInput(obj.pomResponseId, fitType, 'tolerance') ? (
								<input
									type="number"
									min={1}
									value={formatNumber(obj?.tolerance)}
									style={{ display: 'block' }}
									disabled={
										isInputDisabled(obj) ||
										!getTargetValue(obj?.sizeValueList, obj?.baseSize)
									}
									onChange={(e) => handleChange(e, obj.pomResponseId, 'tolerance', fitType)}
									name="tolerance"
									onBlur={(e) =>
										updateToleranceOrActualValue(
											e,
											obj.pomResponseId,
											'TOLERANCE_UPDATE',
											obj.fitTypeId
										)
									}
									onKeyPress={validateFloatNumber}
									onKeyDown={(e) => onPressKey(e, obj, fitType, 'TOLERANCE_UPDATE')}
									autoFocus={true}
								/>
							) : (
								<p>{formatNumber(obj?.tolerance)}</p>
							)}
						</td>
					)}
					{renderSizeValueList(obj, fitType)}
					{hasShowActualAndDiffColumn && (
						<td
							onClick={() =>
								handleSelectedRow(
									obj.pomResponseId,
									'actualValue',
									fitType,
									formatNumber(obj?.actualValue)
								)
							}
							className={isModified(obj?.actualValueModified)}
						>
							{isShowInput(
								obj.pomResponseId,
								fitType,
								'actualValue',
								formatNumber(obj?.actualValue)
							) ? (
								<input
									type="number"
									min={1}
									name="actualValue"
									autoFocus={true}
									disabled={
										isInputDisabled(obj) ||
										!getTargetValue(obj?.sizeValueList, obj?.baseSize)
									}
									value={formatNumber(obj?.actualValue)}
									style={{ display: 'block' }}
									onChange={(e) =>
										handleChange(e, obj.pomResponseId, 'actualValue', fitType)
									}
									onBlur={(e) =>
										updateToleranceOrActualValue(
											e,
											obj.pomResponseId,
											'ACTUAL_VALUE_UPDATE',
											obj.fitTypeId
										)
									}
									onKeyPress={validateFloatNumber}
									onKeyDown={(e) => onPressKey(e, obj, fitType, 'ACTUAL_VALUE_UPDATE')}
								/>
							) : (
								<p>{formatNumber(obj?.actualValue)}</p>
							)}
						</td>
					)}
					{hasShowActualAndDiffColumn && (
						<td
							className={`difference-td ${
								Math.abs(difference) > Math.abs(formatNumber(obj?.tolerance))
									? 'wrong-input'
									: ''
							}`}
						>
							<p className="red">
								{difference === 0
									? ''
									: difference > 0
										? `+${formatNumber(difference)}`
										: formatNumber(difference)}
							</p>
						</td>
					)}
					<td
						className={`review ${isModified(obj?.remarksModified)}`}
						onClick={() => handleSelectedRow(obj.pomResponseId, 'remarks', fitType, obj?.remarks)}
					>
						{isShowInput(obj.pomResponseId, fitType, 'remarks') ? (
							<textarea
								className="table-column-textarea"
								autoFocus={true}
								name="remarks"
								disabled={isInputDisabled(obj)}
								value={obj?.remarks}
								onChange={(e) => handleChange(e, obj.pomResponseId, 'remarks', fitType)}
								onBlur={(e) =>
									handleOnBlurChangeLog(
										e,
										'POINT_REMARKS_UPDATE',
										'remarks',
										obj.pomResponseId,
										obj.fitTypeId
									)
								}
								onKeyDown={(e) => onPressKey(e, obj, fitType, 'POINT_REMARKS_UPDATE')}
							/>
						) : (
							obj?.remarks
						)}
					</td>
				</React.Fragment>
			);
		});
	};

	const handleChange = (e, pomResponseId, fieldName, fitType) => {
		e.preventDefault();
		if (isEditable()) {
			dispatch(
				modifyMeasurementTableCellValue({
					pointId: pomResponseId,
					fieldName,
					fitType,
					value: e.target.value
				})
			);
		}
	};

	const handleSelectedRow = (pomResponseId = null, fieldType = '', fitType = '', defaultValue) => {
		if (isEditable()) {
			setSelectedRow({
				pomResponseId: pomResponseId,
				fieldType: fieldType,
				fitType: fitType,
				defaultValue
			});
		}
	};

	const onPomRemove = (item) => {
		if (isEditable()) {
			let changeLogInfo = {
				id: fitId,
				changeLogType: 'POINT_REMOVE',
				dto: {
					pointOfMeasurementId: item.libraryPOMId || item?.id
				}
			};
			dispatch(saveProductMeasurementChangeLog(changeLogInfo));
			dispatch(removePointInMeasurementTable(item?.id));
		}
	};

	const handleChangePomResponse = (e, pointId) => {
		if (isEditable()) {
			dispatch(
				modifyMeasurementTablePointName({
					pointId,
					value: e.target.value
				})
			);
		}
	};

	const handleOnBlurChangeLog = async (e, logType, fieldName, pointId, fitTypeId) => {
		if (isEditable() && selectedRow?.defaultValue !== e.target.value) {
			let changeLogInfo = {
				// id: fitId,
				changeLogType: logType,
				id: fitTypeId,
				dto: {
					pointOfMeasurementId: pointId,
					[fieldName]: e.target.value || ''
				}
			};
			await dispatch(saveProductMeasurementChangeLog(changeLogInfo));
		}
	};

	const handleDragStart = (e, index) => {
		e.dataTransfer.setData('text/plain', index);
	};

	const handleDragOver = (e) => {
		e.preventDefault();
	};

	const handleDrop = (e, endIndex, item) => {
		if (isEditable()) {
			e.preventDefault();
			const startIndex = e.dataTransfer.getData('text/plain');
			if (startIndex >= 0 && endIndex >= 0) {
				dispatch(
					reArrangeMeasurementTable({
						destinationIndex: endIndex,
						sourceIndex: startIndex,
						fitId: item.fitInfo['FIRST_FIT'].fitTypeId
					})
				);
			}
		}
	};

	const onPOMSelect = (obj) => {
		const [pomCode, name] = obj.value.split(': ');
		onReplacePOM(obj.id, showReplaceDropdownId);
		const updatedItem = tableData.find((item) => item.pomResponse.id === showReplaceDropdownId);
		if (updatedItem) {
			dispatch(
				modifyMeasurementTablePointCode({
					pointId: showReplaceDropdownId,
					value: pomCode
				})
			);
			dispatch(
				modifyMeasurementTablePointName({
					pointId: showReplaceDropdownId,
					value: name
				})
			);
			dispatch(
				modifyMeasurementTablePointIds({
					pointId: showReplaceDropdownId,
					value: obj.id,
					referenceId: obj.referenceId
				})
			);
		}

		setShowReplaceDropdownId(null);
	};

	const renderTableBody = () => {
		return tableData
			?.filter((item) => item?.pomResponse?.id)
			?.map((item, index) => {
				if (item?.pomResponse?.id) {
					return (
						<tr
							key={`measurement_table_row_${index}`}
							onDragStart={(e) => handleDragStart(e, index)}
							onDragOver={handleDragOver}
							onDrop={(e) => handleDrop(e, index, item)}
							draggable
							className={
								item?.pomResponseModified || item?.pomResponseOrderingModified
									? 'highlight-row'
									: ''
							}
						>
							<td>
								<div className="d-flex justify-content-between align-items-center">
									<span>
										{isEditable() && showReplaceDropdownId === item?.pomResponse?.id ? (
											<div className="transparent-select">
												<CustomDropdown
													isShowLabel={false}
													searchPlaceholder={'POM code or name'}
													toolTipLength={18}
													items={getAllPomOptions()}
													onItemClick={onPOMSelect}
												/>
											</div>
										) : (
											<span
												onClick={() =>
													setShowReplaceDropdownId(item?.pomResponse?.id)
												}
											>
												{item?.pomResponse?.pomCode || item?.pomResponse?.id}
											</span>
										)}
									</span>
									{isEditable() && (
										<div className="option">
											<div className="dropdown">
												<button
													className="btn btn-default dropdown-toggle p-0"
													type="button"
													id="menu1"
													data-toggle="dropdown"
													aria-expanded="false"
												>
													<img
														src={`${process.env.PUBLIC_URL}/icons/more_vertical.svg`}
														alt="tee"
													/>
												</button>

												<ul
													className="dropdown-menu dropdown-menu-right shadow-lg"
													role="menu"
													aria-labelledby="menu1"
													x-placement="bottom-end"
												>
													{item?.pomResponse?.referenceId && (
														<li
															onClick={() =>
																setSelectedPom(item?.pomResponse?.referenceId)
															}
														>
															<span>View POM</span>
														</li>
													)}

													{!item?.pomResponse?.isDuplicated &&
														item?.pomResponse?.referenceId && (
															<li
																onClick={() =>
																	onDuplicateMeasurement(item, index)
																}
															>
																<span>Duplicate</span>
															</li>
														)}
													<li
														onClick={() =>
															setShowReplaceDropdownId(item?.pomResponse?.id)
														}
													>
														<span>Replace</span>
													</li>
													<li onClick={() => onPomRemove(item.pomResponse)}>
														<span role="menuitem" className=" red">
															Remove
														</span>
													</li>
												</ul>
											</div>
										</div>
									)}
								</div>
							</td>
							<td className={isModified(item?.pomResponseNameModified)}>
								<div className="measurement-row-delete d-flex justify-content-between">
									<div>
										{selectedRow.pomResponseId === item?.pomResponse?.id &&
										selectedRow.fieldType === 'pomName' ? (
											<textarea
												className="table-column-textarea"
												autoFocus={true}
												name="pomName"
												disabled={isInputDisabled(item.fitInfo['FIRST_FIT'])}
												value={item?.pomResponse?.name}
												onChange={(e) =>
													handleChangePomResponse(e, item.pomResponse.id)
												}
												onBlur={(e) =>
													handleOnBlurChangeLog(
														e,
														'POINT_NAME_UPDATE',
														'name',
														item.pomResponse.id,
														item.fitInfo['FIRST_FIT'].fitTypeId
													)
												}
												onKeyDown={(e) =>
													onPressKey(
														e,
														{
															pomResponseId: item.pomResponse.id,
															fitTypeId: item.fitInfo['FIRST_FIT'].fitTypeId
														},
														undefined,
														'POINT_NAME_UPDATE'
													)
												}
											/>
										) : (
											<span
												className="d-inline-block align-middle"
												onClick={() =>
													handleSelectedRow(
														item?.pomResponse?.id,
														'pomName',
														'',
														item?.pomResponse?.name
													)
												}
											>
												{item?.pomResponse?.name}
											</span>
										)}
									</div>
								</div>
							</td>
							{renderTableCell(item.fitInfo)}
						</tr>
					);
				}
			});
	};

	const getCurrentFitName = (fitTypeId) => {
		let currentFit = null;

		for (let item in tableData[0].fitInfo) {
			if (tableData[0].fitInfo[item].fitTypeId === fitTypeId) {
				currentFit = item;
			}
		}
		return currentFit;
	};

	const copyColumn = (sizeId, fitTypeId) => {
		if (tableData?.length > 0) {
			let currentFit = getCurrentFitName(fitTypeId);
			if (!currentFit) return;

			let selectedSizeValues = [];
			for (let item of tableData) {
				let sizeList = item.fitInfo[currentFit].sizeValueList;
				for (let size of sizeList) {
					if (size.id === sizeId) {
						selectedSizeValues.push(size.value);
					}
				}
			}

			if (selectedSizeValues.length > 0) {
				localStorage.setItem('MEASUREMENT_SIZE_VALUES', JSON.stringify(selectedSizeValues));
				toast.success('Size added in clipboard!');
			}
			setSelectedSizeId(null);
			setSelectedFitTypeId(null);
		}
	};

	const pasteColumn = async (sizeId, fitTypeId) => {
		if (isEditable()) {
			let sizeValues = localStorage.getItem('MEASUREMENT_SIZE_VALUES');
			if (sizeValues) {
				sizeValues = JSON.parse(sizeValues);
				if (sizeValues.length > 0) {
					let currentFit = getCurrentFitName(fitTypeId);
					if (!currentFit) return;
					let bulkChangeLogs = [];
					let modifiedSizeList = {};
					let index = 0;
					for (let size of sizeValues) {
						if (index < tableData.length) {
							let item = tableData[index];
							let obj = item.fitInfo[currentFit];
							let changeLogInfo = {
								changeLogType: 'MEASUREMENT_VALUE_UPDATE',
								id: obj.fitTypeId,
								dto: {
									pointOfMeasurementId: obj.pomResponseId,
									value: size ? size : null,
									sizeId: sizeId
								}
							};
							modifiedSizeList[obj.pomResponseId] = size;
							bulkChangeLogs.push(changeLogInfo);
							index++;
						} else {
							break;
						}
					}
					await dispatch(saveProductMeasurementBulkChangeLog(bulkChangeLogs));
					await dispatch(pasteMeasurementSizeValues({ sizeId, modifiedSizeList, currentFit }));
					setSelectedSizeId(null);
					setSelectedFitTypeId(null);
				}
			}
		}
	};

	const clearColumn = async (sizeId, fitTypeId) => {
		if (isEditable()) {
			let bulkChangeLogs = [];
			let modifiedSizeList = {};
			let currentFit = getCurrentFitName(fitTypeId);
			if (!currentFit) return;
			for (let item of tableData) {
				let sizeList = item.fitInfo[currentFit].sizeValueList;
				for (let size of sizeList) {
					if (size.id === sizeId) {
						let obj = item.fitInfo[currentFit];
						let changeLogInfo = {
							changeLogType: 'MEASUREMENT_VALUE_UPDATE',
							id: obj.fitTypeId,
							dto: {
								pointOfMeasurementId: obj.pomResponseId,
								value: null,
								sizeId: sizeId
							}
						};
						modifiedSizeList[obj.pomResponseId] = '';
						bulkChangeLogs.push(changeLogInfo);
					}
				}
			}
			await dispatch(saveProductMeasurementBulkChangeLog(bulkChangeLogs));
			await dispatch(pasteMeasurementSizeValues({ sizeId, modifiedSizeList, currentFit }));
			setSelectedSizeId(null);
			setSelectedFitTypeId(null);
		}
	};

	const renderSizeDropDown = (sizeId, fitTypeId) => {
		if (selectedSizeId === sizeId && selectedFitTypeId === fitTypeId) {
			return (
				<div className="dropdown-menu show action-heading-dropdown" ref={sizeDropDownRef}>
					<ul>
						<li onClick={() => copyColumn(sizeId, fitTypeId)}>
							<span>Copy column</span>
						</li>
						{isEditable() && (
							<>
								<li onClick={() => pasteColumn(sizeId, fitTypeId)}>
									<span>Paste column</span>
								</li>
								<li onClick={() => clearColumn(sizeId, fitTypeId)}>
									<span>Clear column</span>
								</li>
							</>
						)}
					</ul>
				</div>
			);
		}
	};

	const renderTableHeading = () => {
		const renderCommonHeading = () => {
			return (
				<>
					{hasShowActualAndDiffColumn && (
						<th>
							<span className="sizename">Actual</span>
						</th>
					)}
					{hasShowActualAndDiffColumn && (
						<th>
							<span className="sizename">Difference</span>
						</th>
					)}
					<th className="review">Review Comment</th>
				</>
			);
		};

		if (Object.keys(fitWiseSizeList).length > 0) {
			const renderSizeList = (item) => {
				const sizes = item.sizeList;
				const baseSize = item.baseSize;
				const fitTypeId = item.fitTypeId;
				return sizes?.map((item, index) => {
					return (
						<th
							key={`table_th_size_${index}`}
							onClick={() => {
								setSelectedSizeId(item.id);
								setSelectedFitTypeId(fitTypeId);
							}}
						>
							<span className="sizename">{item.label}</span>
							{item.id === baseSize && <p>(Target)</p>}
							{renderSizeDropDown(item.id, fitTypeId)}
						</th>
					);
				});
			};

			return Object.keys(fitWiseSizeList)?.map((item, index) => {
				return (
					<React.Fragment key={`table_th_${index}`}>
						{renderSizeList(fitWiseSizeList[item])}
						{renderCommonHeading()}
					</React.Fragment>
				);
			});
		} else {
			return <>{renderCommonHeading()}</>;
		}
	};

	const handleHorizontalScroll = (element, speed, distance, step) => {
		let scrollAmount = 0;
		const slideTimer = setInterval(() => {
			element.scrollLeft += step;
			scrollAmount += Math.abs(step);
			if (scrollAmount >= distance) {
				clearInterval(slideTimer);
			}
			if (element.scrollLeft === 0) {
				setArrowDisable(true);
			} else {
				setArrowDisable(false);
			}
		}, speed);
	};
	return (
		<>
			{isShowSlideIcon && (
				<div className="scroll-table-buttons on-measurement">
					<button
						className="left"
						onClick={() => {
							handleHorizontalScroll(elementRef.current, 25, 100, -400);
						}}
						disabled={arrowDisable}
					>
						<SlideIcon />
					</button>
					&nbsp;&nbsp;
					<button
						className="right"
						onClick={() => {
							handleHorizontalScroll(elementRef.current, 25, 100, 400);
						}}
					>
						<SlideIcon />
					</button>
				</div>
			)}
			<div className="measurement-info-table scroll-x-label" ref={elementRef}>
				<table className={`${!isEditable() ? 'edit-table-hover' : ''} table auto`}>
					<thead>
						{tableData.length > 0 && <tr className="title">{renderTableTitle()}</tr>}
						<tr className="heading-row">
							<th>CODE</th>
							<th>Points</th>
							<th>TOL(+/-)</th>
							{renderTableHeading()}
						</tr>
					</thead>
					<tbody onBlur={() => handleSelectedRow()}>{renderTableBody()}</tbody>
				</table>
			</div>
			{selectedPom && <PomViewModal pomId={selectedPom} onClose={() => setSelectedPom(null)} />}
		</>
	);
};

export default BuyerAndNitexMeasurementTable;
